import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { timeLogsAPI, tasksAPI, categoriesAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { format, addDays, subDays, startOfWeek, endOfWeek } from 'date-fns';
import { toast } from 'sonner';
import {
  Clock,
  Plus,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Trash2,
  ClipboardList,
  Loader2,
  Pencil,
  Tag
} from 'lucide-react';
import Layout from '../components/Layout';
import { TranslatableSpan } from '../components/TranslatableText';

const TimeLogPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timeLogs, setTimeLogs] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Edit mode state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  
  // Get the default start time based on existing entries
  const getDefaultStartTime = (logs = timeLogs) => {
    if (logs.length > 0) {
      // Sort by end_time descending to get the latest entry
      const sortedLogs = [...logs].sort((a, b) => {
        if (!a.end_time || !b.end_time) return 0;
        return b.end_time.localeCompare(a.end_time);
      });
      return sortedLogs[0]?.end_time || '08:00';
    }
    return '08:00';
  };

  const [newEntry, setNewEntry] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: '08:00',
    end_time: '17:00',
    hours: 8,
    work_order_id: '',
    category_id: '',
    description: ''
  });

  const fetchTimeLogs = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await timeLogsAPI.getByDate(dateStr);
      setTimeLogs(response.data);
      
      // Update default start time based on fetched logs
      const defaultStart = getDefaultStartTime(response.data);
      setNewEntry(prev => ({
        ...prev,
        start_time: defaultStart,
        hours: calculateHours(defaultStart, prev.end_time)
      }));
    } catch (err) {
      console.error('Failed to fetch time logs:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  useEffect(() => {
    fetchTimeLogs();
    fetchWorkOrders();
    fetchCategories();
  }, [selectedDate, fetchTimeLogs]);

  const fetchWorkOrders = async () => {
    try {
      const response = await tasksAPI.getAll();
      setWorkOrders(response.data.filter(t => t.status !== 'completed'));
    } catch (err) {
      console.error('Failed to fetch work orders:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoriesAPI.getAll();
      setCategories(response.data);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    return Math.max(0, (endMins - startMins) / 60);
  };

  const handleTimeChange = (field, value) => {
    const updated = { ...newEntry, [field]: value };
    if (field === 'start_time' || field === 'end_time') {
      updated.hours = calculateHours(updated.start_time, updated.end_time);
    }
    setNewEntry(updated);
  };

  const handleSaveEntry = async () => {
    if (!newEntry.description.trim()) {
      toast.error('Please add a description of work done');
      return;
    }
    if (newEntry.hours <= 0) {
      toast.error('Hours must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      await timeLogsAPI.create({
        ...newEntry,
        date: format(selectedDate, 'yyyy-MM-dd'),
        work_order_id: newEntry.work_order_id || null,
        category_id: newEntry.category_id || null
      });
      toast.success('Time logged successfully');
      
      // Set the next entry's start time to this entry's end time
      const nextStartTime = newEntry.end_time;
      
      setDialogOpen(false);
      setNewEntry({
        date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: nextStartTime,
        end_time: '17:00',
        hours: calculateHours(nextStartTime, '17:00'),
        work_order_id: '',
        category_id: '',
        description: ''
      });
      fetchTimeLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to log time');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (logId) => {
    try {
      await timeLogsAPI.delete(logId);
      toast.success('Entry deleted');
      fetchTimeLogs();
    } catch (err) {
      toast.error('Failed to delete entry');
    }
  };

  // Edit entry handlers
  const handleOpenEdit = (log) => {
    setEditingLog({
      id: log.id,
      start_time: log.start_time || '08:00',
      end_time: log.end_time || '17:00',
      hours: log.hours || 0,
      work_order_id: log.work_order_id || '',
      category_id: log.category_id || '',
      description: log.description || ''
    });
    setEditDialogOpen(true);
  };

  const handleEditTimeChange = (field, value) => {
    const updated = { ...editingLog, [field]: value };
    if (field === 'start_time' || field === 'end_time') {
      updated.hours = calculateHours(updated.start_time, updated.end_time);
    }
    setEditingLog(updated);
  };

  const handleSaveEdit = async () => {
    if (!editingLog.description.trim()) {
      toast.error('Please add a description of work done');
      return;
    }
    if (editingLog.hours <= 0) {
      toast.error('Hours must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      await timeLogsAPI.update(editingLog.id, {
        start_time: editingLog.start_time,
        end_time: editingLog.end_time,
        hours: editingLog.hours,
        work_order_id: editingLog.work_order_id || null,
        category_id: editingLog.category_id || null,
        description: editingLog.description
      });
      toast.success('Time entry updated');
      setEditDialogOpen(false);
      setEditingLog(null);
      fetchTimeLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update entry');
    } finally {
      setSaving(false);
    }
  };

  const totalHours = timeLogs.reduce((sum, log) => sum + (log.hours || 0), 0);

  const goToPreviousDay = () => setSelectedDate(prev => subDays(prev, 1));
  const goToNextDay = () => setSelectedDate(prev => addDays(prev, 1));
  const goToToday = () => setSelectedDate(new Date());

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white">
              {t('timeLog.title')}
            </h1>
            <p className="text-slate-400 mt-1">{t('timeLog.subtitle')}</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            if (open) {
              // When opening, set start time to last entry's end time
              const defaultStart = getDefaultStartTime();
              setNewEntry(prev => ({
                ...prev,
                start_time: defaultStart,
                hours: calculateHours(defaultStart, prev.end_time)
              }));
            }
            setDialogOpen(open);
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary flex items-center gap-2" data-testid="log-hours-btn">
                <Plus className="w-5 h-5" />
                {t('timeLog.logHours')}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700 max-w-md" aria-describedby="log-hours-description">
              <DialogHeader>
                <DialogTitle className="text-white">{t('timeLog.logHours')}</DialogTitle>
              </DialogHeader>
              <p id="log-hours-description" className="sr-only">Enter your work hours for the day</p>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 uppercase text-xs tracking-wider">Start Time</Label>
                    <Input
                      type="time"
                      value={newEntry.start_time}
                      onChange={(e) => handleTimeChange('start_time', e.target.value)}
                      className="input-industrial mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 uppercase text-xs tracking-wider">End Time</Label>
                    <Input
                      type="time"
                      value={newEntry.end_time}
                      onChange={(e) => handleTimeChange('end_time', e.target.value)}
                      className="input-industrial mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">
                    Total Hours: <span className="text-cyan-400 font-bold">{newEntry.hours.toFixed(1)} hrs</span>
                  </Label>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Work Order (Optional)</Label>
                  <Select value={newEntry.work_order_id || 'general'} onValueChange={(v) => setNewEntry(prev => ({ ...prev, work_order_id: v === 'general' ? '' : v }))}>
                    <SelectTrigger className="input-industrial mt-1">
                      <SelectValue placeholder="Select a work order..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      <SelectItem value="general">General Work</SelectItem>
                      {workOrders.map((wo) => (
                        <SelectItem key={wo.id} value={wo.id}>
                          {wo.work_order_number}: {wo.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Category (Optional)</Label>
                  <Select value={newEntry.category_id || 'none'} onValueChange={(v) => setNewEntry(prev => ({ ...prev, category_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="input-industrial mt-1">
                      <SelectValue placeholder="Select a category..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Description *</Label>
                  <Textarea
                    value={newEntry.description}
                    onChange={(e) => setNewEntry(prev => ({ ...prev, description: e.target.value }))}
                    className="input-industrial mt-1"
                    rows={3}
                    placeholder="What did you work on?"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="btn-secondary">
                  Cancel
                </Button>
                <Button onClick={handleSaveEntry} disabled={saving} className="btn-primary">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Hours'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Date Navigation */}
        <div className="card-industrial">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Navigation arrows and date - stacked on mobile */}
            <div className="flex items-center justify-between w-full sm:w-auto">
              <Button variant="ghost" onClick={goToPreviousDay} className="text-slate-400 hover:text-white p-2">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" className="text-white hover:bg-slate-700 px-2">
                    <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-cyan-400" />
                    <span className="font-bold text-sm sm:text-lg">
                      {format(selectedDate, 'EEE, MMM d, yyyy')}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-slate-800 border-slate-700">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    className="bg-slate-800"
                  />
                </PopoverContent>
              </Popover>
              
              <Button variant="ghost" onClick={goToNextDay} className="text-slate-400 hover:text-white p-2">
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>
            
            {/* Today button - separate row on mobile */}
            <Button variant="outline" onClick={goToToday} className="btn-secondary text-sm w-full sm:w-auto">
              Today
            </Button>
          </div>
          
          <div className="mt-4 pt-4 border-t border-slate-700 flex justify-between items-center">
            <span className="text-slate-400">Total Hours</span>
            <span className="text-2xl font-bold text-cyan-400">{totalHours.toFixed(1)} hrs</span>
          </div>
        </div>

        {/* Time Log Entries */}
        <div className="space-y-4">
          {loading ? (
            <div className="card-industrial flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : timeLogs.length > 0 ? (
            timeLogs.map((log) => (
              <div 
                key={log.id} 
                className="card-industrial cursor-pointer hover:border-cyan-500/50 transition-colors" 
                data-testid={`time-log-${log.id}`}
                onClick={() => handleOpenEdit(log)}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-cyan-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        {log.start_time && log.end_time && (
                          <span className="text-white font-mono text-sm sm:text-base">
                            {log.start_time} - {log.end_time}
                          </span>
                        )}
                        <span className="text-cyan-400 font-bold text-sm sm:text-base">({log.hours.toFixed(1)} hrs)</span>
                      </div>
                      {log.work_order_number && (
                        <div 
                          className="text-amber-400 text-sm cursor-pointer hover:underline mb-1 truncate"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/tasks/${log.work_order_id}`);
                          }}
                        >
                          <ClipboardList className="w-4 h-4 inline mr-1" />
                          {log.work_order_number}: <TranslatableSpan text={log.work_order_title} />
                        </div>
                      )}
                      <p className="text-slate-300 text-sm sm:text-base"><TranslatableSpan text={log.description} /></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-start">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenEdit(log);
                      }}
                      className="text-slate-500 hover:text-cyan-400 p-2"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteEntry(log.id);
                      }}
                      className="text-slate-500 hover:text-red-400 p-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="card-industrial text-center py-12">
              <Clock className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">No hours logged for this day</p>
              <p className="text-slate-500 text-sm mt-1">Click "Log Hours" to add your first entry</p>
            </div>
          )}
        </div>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-slate-800 border-slate-700 max-w-md" aria-describedby="edit-log-description">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Time Entry</DialogTitle>
            </DialogHeader>
            <p id="edit-log-description" className="sr-only">Edit your work hours entry</p>
            {editingLog && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-400 uppercase text-xs tracking-wider">Start Time</Label>
                    <Input
                      type="time"
                      value={editingLog.start_time}
                      onChange={(e) => handleEditTimeChange('start_time', e.target.value)}
                      className="input-industrial mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-400 uppercase text-xs tracking-wider">End Time</Label>
                    <Input
                      type="time"
                      value={editingLog.end_time}
                      onChange={(e) => handleEditTimeChange('end_time', e.target.value)}
                      className="input-industrial mt-1"
                    />
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">
                    Total Hours: <span className="text-cyan-400 font-bold">{editingLog.hours.toFixed(1)} hrs</span>
                  </Label>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Work Order (Optional)</Label>
                  <Select value={editingLog.work_order_id || 'general'} onValueChange={(v) => setEditingLog(prev => ({ ...prev, work_order_id: v === 'general' ? '' : v }))}>
                    <SelectTrigger className="input-industrial mt-1">
                      <SelectValue placeholder="Select a work order..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      <SelectItem value="general">General Work</SelectItem>
                      {workOrders.map((wo) => (
                        <SelectItem key={wo.id} value={wo.id}>
                          {wo.work_order_number}: {wo.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Category (Optional)</Label>
                  <Select value={editingLog.category_id || 'none'} onValueChange={(v) => setEditingLog(prev => ({ ...prev, category_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="input-industrial mt-1">
                      <SelectValue placeholder="Select a category..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-slate-400 uppercase text-xs tracking-wider">Description *</Label>
                  <Textarea
                    value={editingLog.description}
                    onChange={(e) => setEditingLog(prev => ({ ...prev, description: e.target.value }))}
                    className="input-industrial mt-1"
                    rows={3}
                    placeholder="What did you work on?"
                  />
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="btn-primary">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TimeLogPage;
